import { allAssets, type ImageAsset } from './imageAssets'

/**
 * The ten partners, spelled exactly as in
 * `backend/migrations/archive/20260615_0004__seed.sql`. `hospitalImages.test.ts` reads
 * that migration and fails if the two ever disagree, so a renamed hospital
 * cannot silently orphan its photographs.
 */
export const HOSPITAL_SLUGS = [
  'britannia-medical-centre',
  'the-bank-hospital',
  'ugmc',
  'imah',
  'luccahealth',
  'jubail-specialist-hospital',
  'bethel-dental',
  'smsh',
  'focos-orthopaedic-hospital',
  'sgmc',
] as const

export type HospitalSlug = (typeof HOSPITAL_SLUGS)[number]

export interface HospitalPhoto {
  asset: ImageAsset
  /**
   * What this particular photograph shows, written by looking at it (M17-T18b).
   * Names the hospital and describes the picture — a sentence that would fit
   * any hospital is a failed description, not a shortcut.
   */
  alt: string
}

export interface HospitalPhotos {
  /** The one photograph that stands for the hospital — card thumb, profile lead. */
  main: HospitalPhoto
  /** Everything else, in a stable order. May be empty. */
  gallery: HospitalPhoto[]
}

/**
 * Four supplied photographs are deliberately absent — two showing identifiable
 * patients, one a Figma layout frame rather than a photograph, one carrying a
 * third-party watermark. They are excluded in `tools/optimise-images.mjs`, with
 * the reasons, so they are never emitted: Vite bundles everything under
 * `src/assets/images/` whether a page references it or not, and "not rendered"
 * would not have meant "not published". Hence the gaps at `luccahealth-01`,
 * `sgmc-04`, `smsh-04` and `jubail-specialist-hospital-02`.
 *
 * Identity is not in question anywhere: no photograph shows signage belonging
 * to a hospital other than the folder it arrived in, which is the correctness
 * rule ADR-0041 actually cares about.
 */

/**
 * One sentence per rendered photograph, written from the picture itself.
 * A file under `hospitals/` that reaches the renderer without an entry here is
 * a build-time error, not a silent empty `alt`.
 */
const DESCRIPTIONS: Record<string, string> = {
  // Bethel Dental — name legible on the building and on
  // the reception wall.
  'hospitals/bethel-dental-main':
    'Bethel Dental seen from the street: a two-storey building faced in white panels and bands of turquoise and green, the clinic name on the roofline and a solar canopy above it.',
  'hospitals/bethel-dental-01':
    'The reception desk at Bethel Dental, with "Bethel Dental — the smile starts here" painted on the teal wall behind the counter.',
  'hospitals/bethel-dental-02':
    'A waiting room at Bethel Dental with turquoise sofas, a low table and chairs for children, and a kente-patterned panel across one wall.',
  'hospitals/bethel-dental-03':
    'A lounge at Bethel Dental: cream and green armchairs around a low wooden table, with roller blinds along a wall of windows.',
  'hospitals/bethel-dental-04':
    'A treatment room at Bethel Dental with a reclining dental chair, an overhead lamp and a wall-mounted X-ray arm.',

  // Britannia Medical Centre — no name visible on the building; identity taken
  // from the supplied folder.
  'hospitals/britannia-medical-centre-main':
    'Britannia Medical Centre in Tema: a low white-panelled building with a tall glazed entrance bay framed in red, behind a hedge and black railings.',
  'hospitals/britannia-medical-centre-01':
    'A corridor at Britannia Medical Centre with overhead signs to the consulting rooms, pharmacy and laboratory, and waiting chairs beside the stairs.',
  'hospitals/britannia-medical-centre-02':
    'An upstairs waiting area at Britannia Medical Centre lined with red and navy leather armchairs behind a glass balustrade.',
  'hospitals/britannia-medical-centre-03':
    'A ward at Britannia Medical Centre with two adjustable beds, blue-patterned privacy curtains and an oxygen concentrator between them.',
  'hospitals/britannia-medical-centre-04':
    'The pharmacy at Britannia Medical Centre: floor-to-ceiling white shelving stocked with boxed medicines, and a pharmacist at a bank of monitors.',

  // FOCOS Orthopaedic Hospital — confirmed by the "FOCOS Pharmacy" sign in -02.
  'hospitals/focos-orthopaedic-hospital-main':
    'FOCOS Orthopaedic Hospital in Pantang: a single-storey red-roofed wing signposted "Surgery", with an ambulance under the covered walkway and lawn in front.',
  'hospitals/focos-orthopaedic-hospital-01':
    'A covered walkway on white columns linking the single-storey wings of FOCOS Orthopaedic Hospital across a lawn, with the car park to one side.',
  'hospitals/focos-orthopaedic-hospital-02':
    'A reception counter at FOCOS Orthopaedic Hospital, a "FOCOS Pharmacy" sign on the front of the desk and a noticeboard on the wall behind.',
  'hospitals/focos-orthopaedic-hospital-03':
    'A treatment bay at FOCOS Orthopaedic Hospital with an adjustable bed, turquoise folding privacy screens, an overhead surgical light and a patient monitor.',
  'hospitals/focos-orthopaedic-hospital-04':
    'A courtyard at FOCOS Orthopaedic Hospital between red-roofed wings signposted "Laundry" and "Cafeteria", with a brick path across the grass.',

  // International Maritime Hospital — name and IMaH crest legible.
  'hospitals/imah-main':
    'International Maritime Hospital in Tema: a white and terracotta block carrying the hospital name and the IMaH anchor crest, with a blue helipad marked by a white cross in the foreground.',
  'hospitals/imah-01':
    'An MRI scanner with an illuminated bore and its patient table in an imaging room at International Maritime Hospital.',
  'hospitals/imah-02':
    'International Maritime Hospital from the air, its blue-roofed wings set around a palm-lined driveway.',
  'hospitals/imah-03':
    'The corner of the tower at International Maritime Hospital, the IMaH crest on the white façade above palms, with the accident and emergency entrance to the right.',
  'hospitals/imah-04':
    'International Maritime Hospital seen from the approach road behind clipped hedges, its name spanning the upper façade.',

  // Jubail Specialist Hospital — name legible in red roofline lettering.
  'hospitals/jubail-specialist-hospital-main':
    'Jubail Specialist Hospital in Accra: a white three-storey building with its name in red lettering along the roofline, seen from the road beneath power lines.',
  'hospitals/jubail-specialist-hospital-01':
    'A white three-storey block at Jubail Specialist Hospital with balustraded balconies and a ramped entrance at ground level.',
  'hospitals/jubail-specialist-hospital-03':
    'Jubail Specialist Hospital framed through bougainvillea leaves, its red roofline lettering visible above the balcony.',
  'hospitals/jubail-specialist-hospital-04':
    'Jubail Specialist Hospital behind its white perimeter wall, a tall palm beside the gate and an information board at the entrance.',
  'hospitals/jubail-specialist-hospital-05':
    'The upper floors of Jubail Specialist Hospital above an arched window bay, its red roofline lettering running out of frame.',

  // LuccaHealth — "LuccaHealth Medical Specialty Center" legible on the parapet.
  'hospitals/luccahealth-main':
    'LuccaHealth in Accra: a curved two-storey colonnade with "LuccaHealth Medical Specialty Center" across the parapet and palms along the front.',
  'hospitals/luccahealth-02':
    'A corridor at LuccaHealth with lime-green walls and pale timber sliding doors to numbered examination rooms, one standing open on equipment.',
  'hospitals/luccahealth-03':
    "A nurses' station at LuccaHealth: a long white counter with stools and a telephone, facing the numbered examination rooms.",
  'hospitals/luccahealth-04':
    'An operating theatre at LuccaHealth with the table under a surgical lamp, an anaesthetic machine and instrument trolleys around it.',
  'hospitals/luccahealth-05':
    'The entrance to LuccaHealth under its colonnade, a tall LuccaHealth banner hung between the columns and palms in front.',

  // Sweden Ghana Medical Centre — confirmed by the logo overlaid on -main.
  'hospitals/sgmc-main':
    'Sweden Ghana Medical Centre in East Legon Hills: a red-tiled single-storey building with a covered drive-in canopy and lawn, the SGMC Cancer Centre logo overlaid on the sky.',
  'hospitals/sgmc-01':
    'The double-height reception hall at Sweden Ghana Medical Centre, with a mezzanine balcony, a long counter and terracotta planters on the floor.',
  'hospitals/sgmc-02':
    'The entrance canopy at Sweden Ghana Medical Centre: white columns over a paved driveway, with pink and yellow walls and potted palms.',
  'hospitals/sgmc-03':
    'A circular wood-panelled reception desk in the double-height lobby at Sweden Ghana Medical Centre.',

  // St. Michael's Specialist Hospital — name on the entrance canopy and roof.
  'hospitals/smsh-main':
    "St. Michael's Specialist Hospital in Lapaz: a curved grey and cream tower of stacked balconies, its name on the canopy over the entrance.",
  'hospitals/smsh-01':
    "A waiting area at St. Michael's Specialist Hospital with rows of dark blue chairs along the walls and a pale table and chairs in the centre.",
  'hospitals/smsh-02':
    "A corridor at St. Michael's Specialist Hospital lit by recessed downlights, framed abstract prints along one wall and a reception counter at the far end.",
  'hospitals/smsh-03':
    "St. Michael's Specialist Hospital from the air, its name across the roof parapet and a drum-shaped wing under a domed roof.",

  // The Bank Hospital — name and crest legible on the tower.
  'hospitals/the-bank-hospital-main':
    'The Bank Hospital in Cantonments: a grey and yellow multi-storey building with the hospital name and crest near the roofline, seen from the street.',
  'hospitals/the-bank-hospital-01':
    'The emergency department at The Bank Hospital, a ceiling sign reading "Resuscitation Area" above the curtained bays.',
  'hospitals/the-bank-hospital-02':
    'A ward corridor at The Bank Hospital hung with framed landscape paintings, a staff desk and potted plants along its length.',
  'hospitals/the-bank-hospital-03':
    'An X-ray room at The Bank Hospital with a ceiling-mounted tube on rails, an upright bucky stand and a radiographic table.',
  'hospitals/the-bank-hospital-04':
    'An operating theatre at The Bank Hospital with twin ceiling-mounted lamps, a C-arm imaging gantry and a bank of monitors on the wall.',
  'hospitals/the-bank-hospital-05':
    'The tower at The Bank Hospital at close range, the hospital name and crest on its grey and yellow façade above palm fronds.',

  // University of Ghana Medical Centre — no name in frame; identity taken from
  // the supplied folder and consistent across all five.
  'hospitals/ugmc-main':
    'University of Ghana Medical Centre at Legon: the curved glass-clad diagnostics block beside a dark-glazed wing, with a planted roundabout in the foreground.',
  'hospitals/ugmc-01':
    'An open ward bay at University of Ghana Medical Centre with curtained cubicles, timber-fronted worktops and numbered bay markers overhead.',
  'hospitals/ugmc-02':
    'The emergency wing at University of Ghana Medical Centre seen from the approach road, its stone-clad façade above an entrance marked "Emergency" in red.',
  'hospitals/ugmc-03':
    'The emergency entrance at University of Ghana Medical Centre with ambulances in the bays and flowering shrubs along the front.',
  'hospitals/ugmc-04':
    'An operating theatre at University of Ghana Medical Centre with twin ceiling-mounted surgical lamps, an operating table and pendant service columns.',
}

/**
 * Photographs are matched to a hospital by the filename
 * `tools/optimise-images.mjs` emitted, not by a second hand-written list.
 * The owner-supplied folder-to-slug table in that script (ADR-0041) is the
 * single place the mapping is asserted; copying it here would only create a
 * second copy to drift.
 */
const NAME = /^hospitals\/(.+)-(main|\d{2})$/

function collect(): Record<HospitalSlug, HospitalPhotos | null> {
  const mains = new Map<string, HospitalPhoto>()
  const galleries = new Map<string, { key: string; photo: HospitalPhoto }[]>()

  for (const [key, asset] of Object.entries(allAssets())) {
    const m = NAME.exec(key)
    if (!m) continue
    const [, slug, role] = m as unknown as [string, string, string]
    const alt = DESCRIPTIONS[key]
    if (!alt) {
      throw new Error(
        `No description for ${key}. Every emitted photograph needs one written from the picture (M17-T18b) — or an entry in WITHHELD in tools/optimise-images.mjs saying why it should not be published at all.`,
      )
    }
    const photo: HospitalPhoto = { asset, alt }
    if (role === 'main') mains.set(slug, photo)
    else {
      const list = galleries.get(slug) ?? []
      list.push({ key, photo })
      galleries.set(slug, list)
    }
  }

  const out = {} as Record<HospitalSlug, HospitalPhotos | null>
  for (const slug of HOSPITAL_SLUGS) {
    const main = mains.get(slug)
    // `null` is the deliberate "keep the neutral placeholder" answer, and the
    // only alternative to real files — never a half-populated entry.
    out[slug] = main
      ? {
          main,
          gallery: (galleries.get(slug) ?? [])
            .sort((a, b) => a.key.localeCompare(b.key))
            .map((g) => g.photo),
        }
      : null
  }
  return out
}

export const HOSPITAL_IMAGES: Readonly<
  Record<HospitalSlug, HospitalPhotos | null>
> = collect()

/** Photographs for a hospital, or null when it has none and keeps the placeholder. */
export function hospitalPhotos(slug: string): HospitalPhotos | null {
  return (
    (HOSPITAL_IMAGES as Record<string, HospitalPhotos | null>)[slug] ?? null
  )
}
